import type { ModelExtension } from './model-stage';

export type PreviewKind = 'image' | 'video' | 'audio' | 'model' | 'other';

const EXTENSION_KINDS: Record<string, PreviewKind> = {
  png: 'image',
  jpg: 'image',
  jpeg: 'image',
  gif: 'image',
  webp: 'image',
  avif: 'image',
  bmp: 'image',
  svg: 'image',
  mp4: 'video',
  m4v: 'video',
  webm: 'video',
  mov: 'video',
  ogv: 'video',
  mp3: 'audio',
  wav: 'audio',
  ogg: 'audio',
  oga: 'audio',
  m4a: 'audio',
  aac: 'audio',
  flac: 'audio',
  opus: 'audio',
  glb: 'model',
  gltf: 'model',
  fbx: 'model',
};

const MODEL_FILE_EXTENSIONS = new Set(['glb', 'gltf', 'fbx']);

const MODEL_MIME_EXTENSIONS: Record<string, ModelExtension> = {
  'model/gltf-binary': '.glb',
  'model/gltf+json': '.gltf',
};

const UNSPECIFIC_MIMES = new Set(['', 'application/octet-stream', 'binary/octet-stream']);

function extensionOf(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot < 0 ? '' : name.slice(dot + 1).toLowerCase();
}

function mediaFamilyOf(type: string): PreviewKind | null {
  if (type.startsWith('image/')) return 'image';
  if (type.startsWith('video/')) return 'video';
  if (type.startsWith('audio/')) return 'audio';
  return null;
}

function baseMimeOf(mime: string): string {
  return mime.split(';')[0].trim().toLowerCase();
}

export function modelExtension(mime: string, name: string): ModelExtension | null {
  const extension = extensionOf(name);
  if (MODEL_FILE_EXTENSIONS.has(extension)) return `.${extension}` as ModelExtension;
  return MODEL_MIME_EXTENSIONS[baseMimeOf(mime)] ?? null;
}

const RICH_PREVIEW_KINDS = new Set<PreviewKind>(['image', 'video']);

function detectedKind(mime: string, name: string): PreviewKind {
  const type = baseMimeOf(mime);
  const family = mediaFamilyOf(type);
  if (family !== null) return family;
  if (type in MODEL_MIME_EXTENSIONS) return 'model';
  if (!UNSPECIFIC_MIMES.has(type)) return 'other';
  return EXTENSION_KINDS[extensionOf(name)] ?? 'other';
}

export function previewKind(mime: string, name: string): PreviewKind {
  const kind = detectedKind(mime, name);
  return RICH_PREVIEW_KINDS.has(kind) ? kind : 'other';
}
